import { getConsentSnapshot, subscribeConsent } from '@/contexts/consent/store';

export type GtmPurpose = 'analytics' | 'advertising';
const CONTAINER_ID = 'GTM-TXJCNVLK';
const SCRIPT_ID = 'enter-home-gtm';
const MAX_EVENTS = 100;
const MAX_AGE_MS = 60_000;
type GtmWindow = Window & { dataLayer?: unknown[]; google_tag_manager?: Record<string, unknown> };
type PendingEvent = { purpose: GtmPurpose; payload: Record<string, unknown>; time: number; onCommitted?: () => void };

/** 标签生命周期只在宿主接入；同意状态仍由 Consent 模块拥有。 */
export function createGtmClient() {
    let owners = 0;
    let loaded = false;
    let loading: Promise<boolean> | null = null;
    let finishLoad: ((loaded: boolean) => void) | null = null;
    let script: HTMLScriptElement | null = null;
    let startup: Record<string, unknown> | null = null;
    let queue: PendingEvent[] = [];
    let unsubscribe: (() => void) | null = null;

    function allowed(purpose: GtmPurpose): boolean {
        const consent = getConsentSnapshot();
        return owners > 0 &&
            (purpose === 'analytics' ? consent.analyticsAllowed : consent.advertisingAllowed);
    }

    function hasAnyConsent(): boolean { return allowed('analytics') || allowed('advertising'); }

    function dataLayer(): unknown[] {
        const target = window as GtmWindow;
        return target.dataLayer ??= [];
    }

    function flush(): void {
        const pending = queue;
        queue = [];
        for (const item of pending) {
            if (!allowed(item.purpose) || Date.now() - item.time > MAX_AGE_MS) continue;
            try { dataLayer().push(item.payload); committed(item.onCommitted); }
            catch { console.warn('[gtm] event failed'); }
        }
    }

    function committed(callback?: () => void): void {
        try { callback?.(); }
        catch { console.warn('[gtm] commit callback failed'); }
    }

    function initialize(): Promise<boolean> {
        if (typeof window === 'undefined' || !hasAnyConsent()) return Promise.resolve(false);
        loaded ||= Boolean((window as GtmWindow).google_tag_manager?.[CONTAINER_ID]);
        if (loaded) { flush(); return Promise.resolve(true); }
        if (loading) return loading;

        let complete!: (value: boolean) => void;
        const pending = new Promise<boolean>((resolve) => { complete = resolve; });
        loading = pending;
        // Consent Store 先同步收紧各 SDK，再更新 Google 值；微任务后才插入启动事件。
        void Promise.resolve().then(() => {
            if (!hasAnyConsent() || loading !== pending) { complete(false); if (loading === pending) loading = null; return; }
            if (!startup) {
                startup = { 'gtm.start': Date.now(), event: 'gtm.js' };
                dataLayer().push(startup);
            }
            script = document.createElement('script');
            script.id = SCRIPT_ID;
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${CONTAINER_ID}`;
            const currentScript = script;
            const finish = (success: boolean) => {
                clearTimeout(timeout);
                currentScript.removeEventListener('load', onLoad);
                currentScript.removeEventListener('error', onError);
                if (loading === pending) {
                    loading = null;
                    finishLoad = null;
                    loaded = success;
                    if (!success) { currentScript.remove(); script = null; }
                    if (success) flush();
                }
                complete(success && hasAnyConsent());
            };
            const onLoad = () => finish(true);
            const onError = () => { console.warn('[gtm] script failed to load'); finish(false); };
            const timeout = setTimeout(onError, 15_000);
            finishLoad = finish;
            currentScript.addEventListener('load', onLoad);
            currentScript.addEventListener('error', onError);
            document.head.appendChild(currentScript);
        }).catch(() => {
            console.warn('[gtm] initialization failed');
            if (loading === pending) { finishLoad?.(false); loading = null; queue = []; }
            complete(false);
        });
        return pending;
    }

    function sync(): void {
        queue = queue.filter((item) => allowed(item.purpose));
        if (hasAnyConsent()) { void initialize(); return; }
        if (!loaded) {
            finishLoad?.(false);
            if (startup && typeof window !== 'undefined') {
                const layer = dataLayer();
                const index = layer.indexOf(startup);
                if (index >= 0) layer.splice(index, 1);
                startup = null;
            }
        }
    }

    return {
        mount(): () => void {
            owners += 1;
            unsubscribe ??= subscribeConsent(sync);
            sync();
            let disposed = false;
            return () => {
                if (disposed) return;
                disposed = true;
                owners -= 1;
                if (owners === 0) { unsubscribe?.(); unsubscribe = null; sync(); }
            };
        },
        /** 等待已授权的 GTM 脚本就绪；复用同一次加载，不额外插脚本。未授权时立即返回 false。 */
        whenReady(): Promise<boolean> {
            if (typeof window === 'undefined' || owners === 0 || !hasAnyConsent()) return Promise.resolve(false);
            return initialize();
        },
        resetIdentity(): void {
            queue = [];
            if (typeof window === 'undefined') return;
            const host = window as GtmWindow & { gtag?: (...args: unknown[]) => void };
            if (!host.dataLayer) return;
            // 只清理已有身份参数，不触发业务事件或启动脚本。
            host.dataLayer.push({ user_id: null, user_data: null, email: null });
            host.gtag?.('set', { user_id: null, user_data: null });
        },
        submit(event: string, properties: Record<string, unknown>, purpose: GtmPurpose, onCommitted?: () => void): boolean {
            if (typeof window === 'undefined' || !event.trim() || !allowed(purpose)) return false;
            const payload = { ...properties, event };
            if (loaded) { try { dataLayer().push(payload); committed(onCommitted); return true; } catch { return false; } }
            if (queue.length === MAX_EVENTS) queue.shift();
            queue.push({ purpose, payload, time: Date.now(), onCommitted });
            void initialize();
            return true;
        },
    };
}

export const gtmClient = createGtmClient();
